import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, XAxis } from 'recharts';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { Skeleton } from '@/components/ui/skeleton';
import { Campaign } from '@/shared/types/campaigns';

interface Props {
  campaign?: Campaign;
  isLoading?: boolean;
}

/**
 * Conversion chart card shown inside the analytics overview panel.
 * Renders a bar chart of the Sent → Delivered → Opened → Replied funnel plus a
 * per-stage breakdown with drop-rate tooltips.
 */
export const CampaignConversionCard = ({ campaign, isLoading }: Props) => {
  const { t } = useTranslation();

  const deliveredDropRate = campaign?.sent_count
    ? Math.round(((campaign.sent_count - campaign.delivered_count) / campaign.sent_count) * 100)
    : 0;
  const openedDropRate = campaign?.delivered_count
    ? Math.round(
        ((campaign.delivered_count - campaign.read_count) / campaign.delivered_count) * 100
      )
    : 0;
  const repliedDropRate = campaign?.read_count
    ? Math.round(((campaign.read_count - campaign.replied_count) / campaign.read_count) * 100)
    : 0;

  const chartData = [
    { stage: t('campaigns.analytics.sent'), count: campaign?.sent_count || 0 },
    { stage: t('campaigns.analytics.delivered'), count: campaign?.delivered_count || 0 },
    { stage: t('campaigns.analytics.opened'), count: campaign?.read_count || 0 },
    { stage: t('campaigns.analytics.replied'), count: campaign?.replied_count || 0 },
  ];

  const chartConfig = {
    count: { label: t('campaigns.analytics.count'), color: 'var(--primary)' },
  } satisfies ChartConfig;

  return (
    <div className="w-full rounded-lg border border-border bg-background lg:w-96">
      <div className="px-6 pt-6 pb-2">
        <h2 className="text-lg leading-none font-semibold text-foreground">
          {t('campaigns.analytics.conversion')}
        </h2>
      </div>
      {isLoading ? (
        <div className="p-6">
          <Skeleton className="h-64 w-full lg:h-68" />
          <div className="mt-6 flex justify-between">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="space-y-1.5">
                <Skeleton className="h-5 w-12" />
                <Skeleton className="h-3 w-10" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="p-0">
          <ChartContainer config={chartConfig} className="h-64 w-full lg:h-68">
            <BarChart
              accessibilityLayer
              data={chartData}
              maxBarSize={55}
              margin={{ top: 20, right: 30, bottom: 20, left: 20 }}
            >
              <CartesianGrid vertical={false} />
              <XAxis dataKey="stage" tickLine={false} tickMargin={10} axisLine={false} />
              <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
              <Bar dataKey="count" fill="var(--color-count)" radius={4} />
            </BarChart>
          </ChartContainer>

          {/* Summary metrics below chart */}
          <div className="mt-6 px-6 pb-6">
            <div className="flex justify-between">
              {/* Sent Column */}
              <div>
                <div className="text-sm leading-6 font-medium text-foreground">
                  {campaign?.sent_count || 0}
                </div>
                <div className="text-sm leading-none text-muted-foreground">
                  {t('campaigns.analytics.sent')}
                </div>
              </div>

              {/* Delivered Column */}
              <div>
                <div className="flex items-center gap-1">
                  <span className="text-sm leading-6 font-medium text-foreground">
                    {campaign?.delivered_count || 0}
                  </span>
                  <InfoTooltip
                    content={
                      <>
                        <p className="font-semibold">{t('campaigns.analytics.drop_rate')}</p>
                        <p>(Sent - Delivered) / Sent</p>
                      </>
                    }
                    size="large"
                  >
                    <span className="cursor-default text-sm leading-none text-muted-foreground">
                      (-{deliveredDropRate}%)
                    </span>
                  </InfoTooltip>
                </div>
                <div className="text-sm leading-none text-muted-foreground">
                  {t('campaigns.analytics.delivered')}
                </div>
              </div>

              {/* Opened Column */}
              <div>
                <div className="flex items-center gap-1">
                  <span className="text-sm leading-6 font-medium text-foreground">
                    {campaign?.read_count || 0}
                  </span>
                  <InfoTooltip
                    content={
                      <>
                        <p className="font-semibold">{t('campaigns.analytics.drop_rate')}</p>
                        <p>(Delivered - Opened) / Delivered</p>
                      </>
                    }
                    size="large"
                  >
                    <span className="cursor-default text-sm leading-none text-muted-foreground">
                      (-{openedDropRate}%)
                    </span>
                  </InfoTooltip>
                </div>
                <div className="text-sm leading-none text-muted-foreground">
                  {t('campaigns.analytics.opened')}
                </div>
              </div>

              {/* Replied Column */}
              <div>
                <div className="flex items-center gap-1">
                  <span className="text-sm leading-6 font-medium text-foreground">
                    {campaign?.replied_count || 0}
                  </span>
                  <InfoTooltip
                    content={
                      <>
                        <p className="font-semibold">{t('campaigns.analytics.drop_rate')}</p>
                        <p>(Opened - Replied) / Opened</p>
                      </>
                    }
                    size="large"
                  >
                    <span className="cursor-default text-sm leading-none text-muted-foreground">
                      (-{repliedDropRate}%)
                    </span>
                  </InfoTooltip>
                </div>
                <div className="text-sm leading-none text-muted-foreground">
                  {t('campaigns.analytics.replied')}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
